// THE ASSET MANIFEST — the contract between the offline bake and the client.
//
// It lives here, in the package both halves already import, for the same
// reason protocol shapes live in @bebop/shared: a build output whose shape is
// declared twice is a build output that will eventually disagree with itself.
//
// The manifest is the ONLY file in apps/web/public/assets whose name is
// stable. Every other file carries a content hash, so a regenerated sheet is
// a new URL and can be cached forever; the manifest is small and must be
// revalidated, which is the standard split and the one the Caddy in front of
// the bundle already applies to Vite's own hashed output.

export interface AssetMapEntry {
  /** Path relative to the manifest — `materials/stucco.albedo.1a2b3c4d.ktx2`. */
  readonly url: string;
  /**
   * Texels across this map. Not necessarily the material's bake size: the
   * normal and ORM sheets ship at half resolution because the codec that
   * keeps a normal map honest costs three times the bytes. See
   * tools/assets/src/ktx2.ts.
   */
  readonly size: number;
  /** Encoded size on disk, bytes. */
  readonly bytes: number;
  /** Whether the file's DFD declares an sRGB transfer function. */
  readonly srgb: boolean;
}

/**
 * The half of a BRDF that is not a texture.
 *
 * Dielectric reflectance and cloth sheen are per-CLASS constants, not per-texel
 * fields — a lime wall has one F0 over its whole run and so does an awning —
 * so shipping them as maps would be three more sheets carrying one number
 * each. They ride here instead: four floats per material, no bytes, and the
 * client injects them as uniforms. See tools/forge/src/response.ts.
 *
 * The measured bands travel with them because they are the only way a reader
 * of a manifest diff can see that a generator change moved a substance.
 */
export interface AssetResponseEntry {
  /** The family, from `ResponseClass` — informational, and readable in a diff. */
  readonly cls: string;
  /** Normal-incidence reflectance of the dielectric half. three hardcodes 0.04. */
  readonly specular: number;
  /** Charlie-lobe sheen strength; 0 on everything that is not woven. */
  readonly sheen: number;
  /** Gain on INDIRECT SPECULAR only — never on the probe's irradiance. */
  readonly env: number;
  /** Measured p2 / p50 / p98 of the shipped roughness channel. */
  readonly rough: readonly [number, number, number];
  /** Measured p2 / p50 / p98 of the shipped metalness channel. */
  readonly metal: readonly [number, number, number];
  /** Fraction of texels at 0.2 < metalness < 0.8 — the "half metal" tell. */
  readonly halfMetal: number;
}

export interface AssetMaterialEntry {
  /** World size of one tile, metres — the triplanar projection scale. */
  readonly meters: number;
  /** Texels across a tile as baked — the albedo's resolution. */
  readonly size: number;
  /**
   * Mean albedo luminance in LINEAR space. `matchLuma()` divides by it, so
   * this number is how the grade survives the move offline: the client never
   * measures the sheet it loaded, it is told what the baker measured.
   */
  readonly meanLinear: number;
  /** What this surface does with light, beyond what its ORM sheet can say. */
  readonly response: AssetResponseEntry;
  readonly maps: {
    readonly albedo: AssetMapEntry;
    readonly normal: AssetMapEntry;
    readonly orm: AssetMapEntry;
  };
}

/**
 * The character: one glTF binary and one texture atlas, shared by every
 * appearance in the catalogue.
 *
 * It is a separate section rather than another "material" because it is a
 * different KIND of thing — the surfaces above are tiled and addressed by
 * name from a dozen render modules, and this is one indivisible model whose
 * pieces are addressed by mesh name inside the file.
 */
export interface AssetCharacterEntry {
  readonly glb: { readonly url: string; readonly bytes: number };
  readonly atlas: {
    readonly albedo: AssetMapEntry;
    readonly normal: AssetMapEntry;
    readonly orm: AssetMapEntry;
  };
  /** Triangle and vertex counts per mesh, for the budget table and the HUD. */
  readonly pieces: Readonly<Record<string, { readonly tris: number; readonly verts: number }>>;
}

/**
 * The vegetation atlas: one sheet, sixteen painted cells, four independent
 * channels (R shade, G thinness, B vein, A coverage).
 *
 * Its own section for the same reason the character has one — it is not a
 * tiled material. It is addressed by CELL, through `floraCellRect()`, and it
 * is the only sheet in the game whose alpha is load-bearing.
 */
export interface AssetFoliageEntry {
  readonly atlas: AssetMapEntry;
  /** Texels across one cell — the number the painters authored against. */
  readonly cellPx: number;
}

export interface AssetManifest {
  /** Bumped when the shape below changes; the client refuses anything else. */
  readonly version: 4;
  readonly generatedAt: string;
  /** How the files were encoded — informational, for a human reading a diff. */
  readonly encoder: string;
  readonly materials: Readonly<Record<string, AssetMaterialEntry>>;
  readonly character?: AssetCharacterEntry;
  readonly foliage?: AssetFoliageEntry;
}

export const ASSET_MANIFEST_VERSION = 4;

/** Where the manifest is served from, and the base every `url` is relative to. */
export const ASSET_BASE = "/assets/";
